import { Typography } from '@mui/material';
import i18next from 'i18next';
import { Trans, useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import { styled } from '@mui/material/styles';
import ClickableLink from '../../components/Text/ClickableLink';
import {
  DeviceJourney,
  DeviceJourneyData,
  LiveNotificationResponse,
  NotificationCounter,
  NotificationCounterData,
  ScheduleJourneyData,
  SimMismatch,
  SimMismatchData,
} from '../../models/NotificationsModels';
import useLiveNotificationsUtils from './useLiveNotificationsUtils';

const Superscript = styled('sup')(() => ({
  lineHeight: '1rem',
}));

interface LiveNotificationsTemplateProps {
  liveNotification: LiveNotificationResponse;
  onNavigate: (id: string) => void;
}

function LiveNotificationsTemplate({ liveNotification, onNavigate }: LiveNotificationsTemplateProps) {
  const { language } = i18next;
  const [t] = useTranslation();
  const {
    isNotificationCounter,
    isDeviceJourney,
    isSimMismatch,
    isScheduleJourney,
    helpUrls,
    isScheduleNewConnectionJourney,
  } = useLiveNotificationsUtils();

  /**
   * Creates a template for a device type journey error
   */
  const createDeviceJourneyTemplate = () => {
    const data = liveNotification.data as DeviceJourneyData;
    if (liveNotification.status === 'ERROR' || liveNotification.status === 'INFO') {
      return (
        <Trans
          defaults={t('common.na')}
          i18nKey={`common.liveNotifications.messages.${liveNotification.status}_${liveNotification.type}`}
          values={{ user: data.user ? `${data.user?.firstName} ${data.user?.lastName}` : '', eSim: data.device?.eid }}
          components={{
            UserLink: (
              <ClickableLink
                onClick={() => onNavigate(liveNotification.id)}
                to={`/esimManager/userDetails/${data.user?.id}`}
              />
            ),
            ESimLink: (
              <ClickableLink
                onClick={() => onNavigate(liveNotification.id)}
                to={`/esimManager/userDetails/${data.user?.id}/deviceDetails/${data.device?.id}?userName=${data.user?.firstName} ${data.user?.lastName} `}
              />
            ),
            HelpLink: (
              <ClickableLink
                onClick={() => onNavigate(liveNotification.id)}
                to={`${import.meta.env.VITE_HELP_SUPPORT_URL}${helpUrls[liveNotification.type as DeviceJourney]}`}
                target="_blank"
              />
            ),
          }}
        />
      );
    }
    if (liveNotification.status === 'SUCCESS') {
      return (
        <Trans
          defaults={t('common.na')}
          i18nKey={`common.liveNotifications.messages.SUCCESS_${liveNotification.type}`}
          values={{ user: data.user ? `${data.user?.firstName} ${data.user?.lastName}` : '', eSim: data.device?.eid }}
          components={{
            UserLink: (
              <ClickableLink
                onClick={() => onNavigate(liveNotification.id)}
                to={`/esimManager/userDetails/${data.user?.id}`}
              />
            ),
            ESimLink: (
              <ClickableLink
                onClick={() => onNavigate(liveNotification.id)}
                to={`/esimManager/userDetails/${data.user?.id}/deviceDetails/${data.device?.id}?userName=${data.user?.firstName} ${data.user?.lastName} `}
              />
            ),
          }}
        />
      );
    }
    return <div />;
  };

  /**
   * Creates a template for the notification counter
   */
  const createNotificationCounterTemplate = () => {
    if (liveNotification.type === NotificationCounter.NOTIFICATION_COUNTER) {
      return (
        <>
          {t(`common.liveNotifications.messages.NOTIFICATION_COUNTER`, {
            counter: (liveNotification.data as NotificationCounterData).counter,
          })}
        </>
      );
    }
    return <div />;
  };

  /**
   * Creates a template for the sim mismatch
   */
  const createSimMismatchTemplate = () => {
    const data = liveNotification.data as SimMismatchData;
    if (liveNotification.type === SimMismatch.SIM_MISMATCH_BETWEEN_EIDS) {
      return (
        <Trans
          defaults={t('common.na')}
          i18nKey={`common.liveNotifications.messages.${liveNotification.type}`}
          values={{
            user: data.user ? `${data.user?.firstName} ${data.user?.lastName}` : '',
            eSim: data.device?.serialNumber,
          }}
          components={{
            UserLink: (
              <ClickableLink
                onClick={() => onNavigate(liveNotification.id)}
                to={`/esimManager/userDetails/${data.user?.id}`}
              />
            ),
            ESimLink: (
              <ClickableLink
                onClick={() => onNavigate(liveNotification.id)}
                to={`/esimManager/userDetails/${data.user?.id}/deviceDetails/${data.device?.id}?userName=${data.user?.firstName} ${data.user?.lastName} `}
              />
            ),
          }}
        />
      );
    }
    return <div />;
  };

  /**
   * Retrieves the ordinal suffix from the passed day
   * @param day
   */
  const getOrdinalSuffix = (day: number): string => {
    if (day >= 11 && day <= 13) return 'th';
    switch (day % 10) {
      case 1:
        return 'st';
      case 2:
        return 'nd';
      case 3:
        return 'rd';
      default:
        return 'th';
    }
  };

  /**
   * Creates a template for the schedule journey
   */
  const createScheduleJourneyTemplate = () => {
    const data = liveNotification.data as ScheduleJourneyData;
    if (data) {
      const date = DateTime.fromISO(
        isScheduleNewConnectionJourney(liveNotification.type) ? data.startDate : data.endDate,
      );
      return (
        <Trans
          defaults={t('common.na')}
          i18nKey={`common.liveNotifications.messages.${liveNotification.status}_${liveNotification.type}`}
          values={{
            user: data.user ? `${data.user?.firstName} ${data.user?.lastName}` : '',
            eSim: data.device?.eid,
            day: date.day,
            suffix: getOrdinalSuffix(date.day),
            monthYear: date.setLocale(language).toFormat('LLLL yy'),
          }}
          components={{
            UserLink: (
              <ClickableLink
                onClick={() => onNavigate(liveNotification.id)}
                to={`/esimManager/userDetails/${data.user?.id}`}
              />
            ),
            ESimLink: (
              <ClickableLink
                onClick={() => onNavigate(liveNotification.id)}
                to={`/esimManager/userDetails/${data.user?.id}/deviceDetails/${data.device?.id}?userName=${data.user?.firstName} ${data.user?.lastName} `}
              />
            ),
            Sup: <Superscript />,
          }}
        />
      );
    }

    return <div />;
  };

  /**
   * Returns the template jsx
   */
  const createTemplate = () => {
    let template = <div />;
    if (isDeviceJourney(liveNotification.type)) {
      template = createDeviceJourneyTemplate();
    } else if (isNotificationCounter(liveNotification.type)) {
      template = createNotificationCounterTemplate();
    } else if (isSimMismatch(liveNotification.type)) {
      template = createSimMismatchTemplate();
    } else if (isScheduleJourney(liveNotification.type)) {
      template = createScheduleJourneyTemplate();
    }
    return template;
  };

  return (
    <Typography
      sx={{
        fontSize: '1rem',
        '> a': {
          fontSize: '1rem',
        },
      }}
    >
      {createTemplate()}
    </Typography>
  );
}

export default LiveNotificationsTemplate;
